// @ts-check
/** @typedef {import('@datahooks/types/organisationOverview.types').OrgOverviewKpiRow} OrgOverviewKpiRow */
import React from 'react';

import { TestableCard } from '@components/general/TestableComponents';
import KpiCard from '@components/cards/shared/KpiCard';
import DeltaChip from '@components/general/DeltaChip';
import LoadingIcon from '@components/navigation/LoadingIcon';

import PropTypes from 'prop-types';

/**
 * OrganisationOverviewKpiSection component displays key performance indicators (KPIs) for the organisation overview page. It receives KPI data as props and renders them in a responsive grid layout.
 * @param {Object} props
 * @param {Object[]} props.projects - An array of project objects based on the applied filters.
 * @param {OrgOverviewKpiRow[]} props.orgKPIsByProject - An array of KPI data for each project in the organisation.
 * @param {boolean} props.isLoading - A flag indicating whether the KPI data is currently being loaded.
 *
 *  @returns {JSX.Element} The rendered OrganisationOverviewKpiSection component.
 */
export default function OrganisationOverviewKpiSection({ projects = [], orgKPIsByProject = [], isLoading }) {
  const kpis = [
    { key: 'total-projects', label: 'Total Projects', value: projects.length ?? 0 },
    { key: 'api-tested', label: 'Total API Tests', value: 0, previousValue: 0 },
    { key: 'ui-tested', label: 'Total UI Tests', value: 0, previousValue: 0 },
    { key: 'open-events', label: 'Open Project Events', value: 0, },
  ];

  let uiTests = 0;
  let uiTestsPrevious = 0;
  let apiTests = 0;
  let apiTestsPrevious = 0;
  let openEvents = 0;

  // Sum the KPI values across all projects, but only for the projects that are in the current filtered projects list
  orgKPIsByProject.forEach((projectKpi) => {

    if (!projects.some((proj) => proj.projectId === projectKpi.projectId)) {
      // If the project associated with this KPI is not in the filtered projects list, skip it
      return;
    }

    uiTests += projectKpi.uiTests || 0;
    apiTests += projectKpi.apiTests || 0;
    openEvents += projectKpi.openEvents || 0;
    uiTestsPrevious += projectKpi.uiTestsPrevious || 0;
    apiTestsPrevious += projectKpi.apiTestsPrevious || 0;
  });

  kpis[1].value = apiTests;
  kpis[1].previousValue = apiTestsPrevious;
  kpis[2].value = uiTests;
  kpis[2].previousValue = uiTestsPrevious;
  kpis[3].value = openEvents;

  if (isLoading) {
    return (
      <TestableCard dataTestId="org-overview-kpi-card" className="p-4 w-full">
        <div className="flex w-full justify-center">
          <LoadingIcon showText />
        </div>
      </TestableCard>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-blue-500" data-testid="org-overview-kpi-grid">
      {kpis.map((kpi) => (
        <KpiCard
          key={kpi.key}
          testIdPrefix="org-overview-kpi"
          testKey={kpi.key}
          label={kpi.label}
          value={kpi.value}
          valueAdornment={
            kpi.previousValue != null && (
              <DeltaChip
                currentValue={kpi.value}
                previousValue={kpi.previousValue}
                dataTestId={`org-overview-kpi-delta-${kpi.key}`}
              />
            )
          }
        />
      ))}
    </div>
  );
}

OrganisationOverviewKpiSection.propTypes = {
  projects: PropTypes.array,
  orgKPIsByProject: PropTypes.arrayOf(
    PropTypes.shape({
      projectId: PropTypes.number.isRequired,
      projectName: PropTypes.string.isRequired,
      apiTests: PropTypes.number.isRequired,
      uiTests: PropTypes.number.isRequired,
      apiTestsPrevious: PropTypes.number.isRequired,
      uiTestsPrevious: PropTypes.number.isRequired,
      openEvents: PropTypes.number.isRequired,
    })
  ),
  isLoading: PropTypes.bool.isRequired,
};